/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Check,
  BarChart3,
  Zap,
  Users,
  Clock,
  TrendingUp,
  Award,
  AlertTriangle,
  TrendingDown,
  Inbox,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useGetUsage } from "@/hooks/use-subscription";
import { B2BPlanType, B2CPlanType } from "@/pages/billing/pricing-data";

// Define interface for the plan metrics data
export interface PlanMetricsData {
  success?: boolean;
  message?: string;
  planName?: any;
  planType?: any;
  avgInboxPlacementScore?: number;
  spamScore?: number;
  emailsTested?: number;
  quantity?: number;
  assignedMailboxes?: number;
  dailyAllowance?: number;
  monthlyAllowance?: number;
  subscriptionDetails?: {
    startDate?: string;
    endDate?: string;
    interval?: string;
    status?: string;
    nextBillingDate?: string;
  };
  usageMetrics?: {
    warmupEmails?: {
      daily?: {
        used?: number;
        total?: number;
        percentage?: number;
      };
      monthly?: {
        used?: number;
        total?: number;
        percentage?: number | null;
      };
    };
    healthTests?: {
      used?: number;
      total?: number;
      percentage?: number | null;
    };
    placementTests?: {
      used?: number;
      total?: number;
      percentage?: number | null;
    };
  };
}

interface FeatureHighlightsProps {
  currentPlan: any;
  planMetrics?: PlanMetricsData;
}


export function FeatureHighlights({
  currentPlan,
  planMetrics,
}: FeatureHighlightsProps) {
  // Add console log to debug plan type
  console.log("Current plan type:", currentPlan?.type, "Plan:", currentPlan);
  
  // Use API data if available, fallback to currentPlan mock data
  const dailyWarmupUsed =
    planMetrics?.usageMetrics?.warmupEmails?.daily?.used ??
    currentPlan.usage.warmupEmails;
  const dailyWarmupTotal =
    planMetrics?.usageMetrics?.warmupEmails?.daily?.total ??
    currentPlan.features.warmupEmails;
  const dailyWarmupPercentage =
    planMetrics?.usageMetrics?.warmupEmails?.daily?.percentage ??
    Math.round(
      (currentPlan.usage.warmupEmails / currentPlan.features.warmupEmails) * 100
    );

  // Monthly warmup emails data
  const monthlyWarmupUsed =
    planMetrics?.usageMetrics?.warmupEmails?.monthly?.used ??
    currentPlan.usage.warmupEmails * 30;
  const monthlyWarmupTotal =
    planMetrics?.usageMetrics?.warmupEmails?.monthly?.total ??
    currentPlan.features.warmupEmails * 30;
  const monthlyWarmupPercentage =
    planMetrics?.usageMetrics?.warmupEmails?.monthly?.percentage ??
    Math.round((monthlyWarmupUsed / monthlyWarmupTotal) * 100);

  const healthTestsUsed =
    planMetrics?.usageMetrics?.healthTests?.used ??
    currentPlan.usage.healthTests;
  const healthTestsTotal =
    planMetrics?.usageMetrics?.healthTests?.total ??
    currentPlan.features.healthTests;
  const healthTestsPercentage =
    planMetrics?.usageMetrics?.healthTests?.percentage ??
    Math.round(
      (currentPlan.usage.healthTests / currentPlan.features.healthTests) * 100
    );

  const inboxPlacementScore = planMetrics?.avgInboxPlacementScore ?? 92.3;
  const spamScore = planMetrics?.spamScore ?? 0.8;

  const { data: usageData } = useGetUsage();

  return (
    <div className="space-y-6">
      {/* ROI and Value Metrics */}
      <div className="bg-gradient-to-r from-primary/10 to-transparent p-4 rounded-lg border border-primary/20 mb-6">
        <h3 className="font-medium flex items-center">
          <TrendingUp className="h-5 w-5 text-primary mr-2" />
          Your Email Performance with {currentPlan.name} plan
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <div className="bg-white bg-opacity-60 dark:bg-secondary p-3 rounded-md">
            <div className="text-sm text-muted-foreground">Inbox Placement</div>
            <div className="text-2xl font-bold text-primary">
              {(inboxPlacementScore).toFixed(2)}%
            </div>
            {/* <div className="text-xs text-green-600 flex items-center mt-1">
              <TrendingUp className="h-3 w-3 mr-1" /> out of 100 results
            </div> */}
          </div>
          <div className="bg-white bg-opacity-60 dark:bg-secondary p-3 rounded-md">
            <div className="text-sm text-muted-foreground">Spam Score</div>
            <div className="text-2xl font-bold text-primary">{(spamScore).toFixed(2)}%</div>
            {/* <div className="text-xs text-green-600 flex items-center mt-1">
              <TrendingDown className="h-3 w-3 mr-1" /> based on{" "}
              {planMetrics?.emailsTested || 1000} tests
            </div> */}
          </div>
          <div className="bg-white bg-opacity-60 dark:bg-secondary p-3 rounded-md">
            <div className="text-sm text-muted-foreground">Domain Rating</div>
            <div className="text-2xl font-bold text-primary">Excellent</div>
            {/* <div className="text-xs text-green-600 flex items-center mt-1">
              <TrendingUp className="h-3 w-3 mr-1" /> 7 out of 10 domains is
              healthy
            </div> */}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border rounded-lg p-4 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-blue-900/20">
          <div className="flex items-start">
            <div className="bg-blue-100 dark:bg-blue-900/20 p-2 rounded-full mr-3">
              <BarChart3 className="h-5 w-5 text-blue-600" />
            </div>
            <div className="w-full">
              <h3 className="font-medium">Email Warmup Per Day</h3>
              <p className="text-sm text-muted-foreground mt-1">
                You're using {dailyWarmupUsed?.toLocaleString()} of your{" "}
                {dailyWarmupTotal?.toLocaleString()} daily warmup emails.
              </p>
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1">
                  <span>Current Usage</span>
                  <span className="text-blue-600 font-medium">
                    {dailyWarmupPercentage.toFixed(2)}%
                  </span>
                </div>
                <Progress
                  value={dailyWarmupPercentage}
                  className="h-2 bg-blue-10 dark:bg-blue-900/20"
                  indicatorClassName="bg-blue-600"
                />
                {dailyWarmupUsed > 500 && (
                  <div className="mt-2 text-xs bg-red-50 text-red-700 p-1.5 rounded border border-red-100 flex items-center">
                    <AlertTriangle className="h-3 w-3 mr-1.5" />
                    <span>
                      Would exceed lower plan limits by {dailyWarmupUsed - 500}{" "}
                      emails/day
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded-lg p-4 bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-purple-900/20">
          <div className="flex items-start">
            <div className="bg-purple-100 dark:bg-purple-900/20 p-2 rounded-full mr-3">
              <BarChart3 className="h-5 w-5 text-purple-600" />
            </div>
            <div className="w-full">
              <h3 className="font-medium">Email Warmups Per Month</h3>
              <p className="text-sm text-muted-foreground mt-1">
                You've used {monthlyWarmupUsed?.toLocaleString()} of your{" "}
                {monthlyWarmupTotal?.toLocaleString()} monthly warmup emails.
              </p>
              <div className="mt-3">
                <div className="flex justify-between text-xs mb-1">
                  <span>Current Usage</span>
                  <span className="text-purple-600 font-medium">
                    {monthlyWarmupPercentage.toFixed(2)}%
                  </span>
                </div>
                <Progress
                  value={monthlyWarmupPercentage}
                  className="h-2 bg-purple-10 dark:bg-purple-900/20"
                  indicatorClassName="bg-purple-600"
                />
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Customer Testimonials */}
      {/* <div className="border rounded-lg p-4">
        <h3 className="font-medium mb-3 flex items-center">
          <Award className="h-5 w-5 text-primary mr-2" />
          What other {currentPlan.name} customers say
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-muted/20 p-3 rounded-md">
            <p className="text-sm italic">
              "Upgrading to the {currentPlan.name} plan increased our email deliverability by 35% and directly improved
              our sales pipeline."
            </p>
            <p className="text-xs text-muted-foreground mt-2">— Marketing Director, SaaS Company</p>
          </div>
          <div className="bg-muted/20 p-3 rounded-md">
            <p className="text-sm italic">
              "The dedicated account manager and API access on the {currentPlan.name} plan saved us countless hours and
              improved our workflow efficiency."
            </p>
            <p className="text-xs text-muted-foreground mt-2">— Head of Operations, E-commerce Brand</p>
          </div>
        </div>
      </div> */}

      {/* Predictive Impact Analysis */}
      {/* <div className="border rounded-lg p-4 bg-gradient-to-r from-red-50 to-transparent">
        <h3 className="font-medium mb-3 flex items-center">
          <TrendingDown className="h-5 w-5 text-red-600 mr-2" />
          Predicted Impact of Downgrading
        </h3>
        <div className="space-y-3">
          <div className="flex items-center">
            <div className="w-40 text-sm">Deliverability Rate</div>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 rounded-full" style={{ width: "18%" }}></div>
            </div>
            <div className="w-20 text-right text-sm text-red-600">-18%</div>
          </div>
          <div className="flex items-center">
            <div className="w-40 text-sm">Inbox Placement</div>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 rounded-full" style={{ width: "12%" }}></div>
            </div>
            <div className="w-20 text-right text-sm text-red-600">-12%</div>
          </div>
          <div className="flex items-center">
            <div className="w-40 text-sm">Campaign Performance</div>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-red-500 rounded-full" style={{ width: "23%" }}></div>
            </div>
            <div className="w-20 text-right text-sm text-red-600">-23%</div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            *Based on performance data from similar customers who downgraded
          </p>
        </div>
      </div> */}

      {/* Value Summary */}
      <div className="border rounded-lg p-4 bg-gradient-to-r from-primary/5 to-transparent">
        <h3 className="font-medium mb-2">Your {currentPlan.name} Plan Value</h3>
        <ul className="space-y-2">
          <li className="flex items-start">
            <Check className="h-4 w-4 text-primary mr-2 mt-0.5 shrink-0" />
            <span className="text-sm">
              <strong>High Volume Capacity:</strong> Your current usage of{" "}
              {dailyWarmupUsed?.toLocaleString()} daily emails would exceed lower
              plan limits by {dailyWarmupUsed > 500 ? dailyWarmupUsed - 500 : 0}{" "}
              emails.
            </span>
          </li>

          {/* <li className="flex items-start">
            <Check className="h-4 w-4 text-primary mr-2 mt-0.5" />
            <span className="text-sm">
              <strong>Advanced Features:</strong> You're benefiting from
              superior inbox placement ({usageData?.maxInboxPlacementTestsUsedThisMonth}) and advanced
              analytics that aren't available in lower plans.
            </span>
          </li> */}
          <li className="flex items-start">
            <Check className="h-4 w-4 text-primary mr-2 mt-0.5 shrink-0" />
            <span className="text-sm">
              <strong>Deliverability Impact:</strong> Your current plan has
              helped improve your email deliverability by an estimated {inboxPlacementScore}% since
              you started, directly impacting your business results.
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
